import { and, asc, desc, eq, ilike, inArray, lt, or, sql } from "drizzle-orm";
import { getVariantsByIds } from "@/modules/products/domain/repo/catalog";
import {
  getConversionFactor,
  normalizeStockUnitLabel,
} from "@/modules/products/domain/unit-conversion";
import type { DbClient } from "@/server/platform/db/client";
import {
  importOrderLines,
  importOrderReceiptLines,
  importOrderReceipts,
  importOrders,
  importOrderTrackings,
  stockLots,
  type ImportOrderLineMatchStatus,
} from "@/server/platform/db/schema/inventory";
import { nowDate } from "@/shared/lib/date-time";
import type { DbTransaction } from "@/shared/types";
import type {
  ImportOrderListQueryDTO,
  InTransitReportQueryDTO,
} from "../import-order-contracts";
import type {
  ImportOrderPhase13DetailDTO,
  ImportOrderPhase13LineDTO,
  ImportOrderPhase13ListItemDTO,
  ImportOrderReceiptDTO,
  ImportOrderTrackingRowDTO,
  InTransitReportByVariantRowDTO,
  VariantImportPriceHistoryRowDTO,
  VariantLastImportPriceDTO,
} from "../types";
import { listGoodsReceiptsByImportOrderId } from "./goods-receipts";
import {
  importLineInTransitRemaining,
  importLineInTransitValueLak,
  MATCHED_IMPORT_LINE_SQL,
  matchedImportLineWhere,
} from "../import-order-line-filters";

type DbLike = DbTransaction | DbClient;

const OPEN_IMPORT_STATUSES = [
  "ordered",
  "at_carrier",
  "partially_received",
] as const;

const remainingQtyExpr = sql<number>`(
  ${importOrderLines.quantityOrdered}
  - ${importOrderLines.quantityReceived}
  - ${importOrderLines.shortQuantity}
)`;

function startOfTodayUtc(): Date {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
}

function mapLineRow(
  row: typeof importOrderLines.$inferSelect,
  units?: {
    stockUnitLabel: string;
    receiveUnitLabel: string | null;
    receiveConversionFactor: number;
  },
): ImportOrderPhase13LineDTO {
  const remainingQuantity =
    row.matchStatus === "skipped"
      ? 0
      : Math.max(
          0,
          row.quantityOrdered - row.quantityReceived - row.shortQuantity,
        );
  const receiveLabel = units?.receiveUnitLabel?.trim() || null;
  return {
    id: row.id,
    importOrderId: row.importOrderId,
    matchStatus: row.matchStatus as ImportOrderLineMatchStatus,
    variantId: row.variantId,
    externalTitle: row.externalTitle,
    externalUrl: row.externalUrl,
    externalImageKey: row.externalImageKey,
    matchedAt: row.matchedAt,
    matchedBy: row.matchedBy,
    skippedAt: row.skippedAt,
    skipNote: row.skipNote,
    description: row.description,
    orderedQuantity: row.quantityOrdered,
    receivedQuantity: row.quantityReceived,
    shortQuantity: row.shortQuantity,
    unitPriceForeign: row.unitPriceForeign,
    goodsCostForeign: row.goodsCostForeign,
    goodsCostLak: row.goodsCostLak,
    allocatedShippingLak: row.allocatedShippingLak,
    landedCostLak: row.landedCostLak,
    unitLandedCostLak: row.unitLandedCostLak,
    productName: row.productName,
    variantName: row.variantName,
    stockUnitLabel: normalizeStockUnitLabel(units?.stockUnitLabel),
    receiveUnitLabel: receiveLabel,
    receiveConversionFactor: receiveLabel
      ? getConversionFactor(units?.receiveConversionFactor)
      : 1,
    remainingQuantity,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}

async function mapLinesWithUnits(
  client: DbLike,
  rows: (typeof importOrderLines.$inferSelect)[],
): Promise<ImportOrderPhase13LineDTO[]> {
  const variantIds = [
    ...new Set(
      rows
        .map((row) => row.variantId)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  const variants = await getVariantsByIds(client, variantIds);
  const variantMap = new Map(variants.map((v) => [v.id, v]));

  return rows.map((row) => {
    const variant = row.variantId ? variantMap.get(row.variantId) : undefined;
    const receiveLabel = variant?.receiveUnitLabel?.trim() || null;
    return mapLineRow(row, {
      stockUnitLabel: normalizeStockUnitLabel(variant?.stockUnitLabel),
      receiveUnitLabel: receiveLabel,
      receiveConversionFactor: receiveLabel
        ? getConversionFactor(variant?.receiveConversionFactor)
        : 1,
    });
  });
}

function mapTrackingRow(
  row: typeof importOrderTrackings.$inferSelect,
): ImportOrderTrackingRowDTO {
  return {
    id: row.id,
    importOrderId: row.importOrderId,
    trackingNo: row.trackingNo,
    carrierName: row.carrierName,
    status: row.status as ImportOrderTrackingRowDTO["status"],
    shippedAt: row.shippedAt,
    arrivedAt: row.arrivedAt,
    note: row.note,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}

function buildPhase13ImportOrderListConditions(query: ImportOrderListQueryDTO) {
  const conditions = [];
  if (query.status) conditions.push(eq(importOrders.status, query.status));
  if (query.source) conditions.push(eq(importOrders.source, query.source));
  if (query.search?.trim()) {
    const term = `%${query.search.trim()}%`;
    conditions.push(
      or(
        ilike(importOrders.externalOrderNo, term),
        ilike(importOrders.orderNumber, term),
      ),
    );
  }
  if (query.overdueOnly) {
    conditions.push(inArray(importOrders.status, [...OPEN_IMPORT_STATUSES]));
    conditions.push(lt(importOrders.expectedArrivalDate, startOfTodayUtc()));
    conditions.push(
      sql`exists (
        select 1 from import_order_lines
        where import_order_lines.import_order_id = import_orders.id
          and ${sql.raw(MATCHED_IMPORT_LINE_SQL)}
          and import_order_lines.quantity_ordered
            > import_order_lines.quantity_received + import_order_lines.short_quantity
      )`,
    );
  }
  return conditions;
}

export async function countPhase13ImportOrders(
  client: DbLike,
  query: ImportOrderListQueryDTO,
): Promise<number> {
  const conditions = buildPhase13ImportOrderListConditions(query);
  const [row] = await client
    .select({ count: sql<number>`cast(count(*) as int)` })
    .from(importOrders)
    .where(conditions.length ? and(...conditions) : undefined);
  return row?.count ?? 0;
}

export async function listPhase13ImportOrders(
  client: DbLike,
  query: ImportOrderListQueryDTO,
): Promise<ImportOrderPhase13ListItemDTO[]> {
  const conditions = buildPhase13ImportOrderListConditions(query);

  let listQuery = client
    .select({
      id: importOrders.id,
      orderNumber: importOrders.orderNumber,
      externalOrderNo: importOrders.externalOrderNo,
      source: importOrders.source,
      status: importOrders.status,
      currency: importOrders.currency,
      exchangeRate: importOrders.exchangeRate,
      shippingTotalLak: importOrders.shippingTotalLak,
      goodsSubtotalForeign: importOrders.goodsSubtotalForeign,
      goodsSubtotalLak: importOrders.goodsSubtotalLak,
      landedTotalLak: importOrders.landedTotalLak,
      orderedAt: importOrders.orderedAt,
      expectedArrivalDate: importOrders.expectedArrivalDate,
      createdAt: importOrders.createdAt,
      updatedAt: importOrders.updatedAt,
      lineCount: sql<number>`(
        select count(*)::int from import_order_lines
        where import_order_lines.import_order_id = import_orders.id
      )`,
      totalOrdered: sql<number>`(
        select coalesce(sum(import_order_lines.quantity_ordered), 0)::int
        from import_order_lines
        where import_order_lines.import_order_id = import_orders.id
      )`,
      totalReceived: sql<number>`(
        select coalesce(sum(import_order_lines.quantity_received), 0)::int
        from import_order_lines
        where import_order_lines.import_order_id = import_orders.id
      )`,
      totalRemaining: sql<number>`(
        select coalesce(sum(greatest(
          import_order_lines.quantity_ordered
          - import_order_lines.quantity_received
          - import_order_lines.short_quantity,
          0
        )), 0)::int
        from import_order_lines
        where import_order_lines.import_order_id = import_orders.id
          and ${sql.raw(MATCHED_IMPORT_LINE_SQL)}
      )`,
      inTransitValueLak: sql<string>`(
        select coalesce(sum(
          (import_order_lines.unit_landed_cost_lak)::numeric
          * greatest(
            import_order_lines.quantity_ordered
            - import_order_lines.quantity_received
            - import_order_lines.short_quantity,
            0
          )
        ), 0)::text
        from import_order_lines
        where import_order_lines.import_order_id = import_orders.id
          and ${sql.raw(MATCHED_IMPORT_LINE_SQL)}
      )`,
      linePreview: sql<string>`(
        select coalesce(string_agg(label, ' ◆ '), '')
        from (
          select distinct on (product_name)
            trim(both from product_name) as label
          from import_order_lines
          where import_order_lines.import_order_id = import_orders.id
          order by product_name
          limit 3
        ) preview
      )`,
      distinctLineCount: sql<number>`(
        select count(*)::int from (
          select distinct product_name
          from import_order_lines
          where import_order_lines.import_order_id = import_orders.id
        ) lines
      )`,
      pendingLineCount: sql<number>`(
        select count(*)::int from import_order_lines
        where import_order_lines.import_order_id = import_orders.id
          and import_order_lines.match_status = 'pending'
      )`,
    })
    .from(importOrders)
    .where(conditions.length ? and(...conditions) : undefined)
    .orderBy(desc(importOrders.createdAt));

  if (query.limit != null) {
    listQuery = listQuery.limit(query.limit);
  }
  if (query.offset != null) {
    listQuery = listQuery.offset(query.offset);
  }

  const rows = await listQuery;

  const today = startOfTodayUtc();

  return rows.map((row) => {
    const isOverdue =
      row.expectedArrivalDate != null &&
      row.expectedArrivalDate < today &&
      (OPEN_IMPORT_STATUSES as readonly string[]).includes(row.status) &&
      row.totalRemaining > 0;

    const previewItems = splitLinePreview(row.linePreview);
    const linePreviewMoreCount = Math.max(
      0,
      row.distinctLineCount - previewItems.length,
    );

    const { linePreview, distinctLineCount, ...rest } = row;

    return {
      ...rest,
      status: row.status as ImportOrderPhase13ListItemDTO["status"],
      source: row.source as ImportOrderPhase13ListItemDTO["source"],
      linePreviewItems: previewItems,
      linePreviewMoreCount,
      isOverdue,
    };
  });
}

const LINE_PREVIEW_DELIMITER = " ◆ ";

function splitLinePreview(raw: string): string[] {
  if (!raw.trim()) return [];
  return raw.split(LINE_PREVIEW_DELIMITER).filter(Boolean);
}

function buildLinePreviewFromLines(
  lines: Array<{ productName: string; variantName: string }>,
  maxItems = 3,
) {
  const labels: string[] = [];
  const seen = new Set<string>();

  for (const line of lines) {
    const label = line.productName.trim();
    if (!label || seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }

  return {
    linePreviewItems: labels.slice(0, maxItems),
    linePreviewMoreCount: Math.max(0, labels.length - maxItems),
  };
}

export async function getPhase13ImportOrderById(
  client: DbLike,
  id: string,
): Promise<ImportOrderPhase13DetailDTO | null> {
  const [row] = await client
    .select()
    .from(importOrders)
    .where(eq(importOrders.id, id));
  if (!row) return null;

  const lines = await client
    .select()
    .from(importOrderLines)
    .where(eq(importOrderLines.importOrderId, id))
    .orderBy(asc(importOrderLines.productName));

  const trackings = await client
    .select()
    .from(importOrderTrackings)
    .where(eq(importOrderTrackings.importOrderId, id))
    .orderBy(asc(importOrderTrackings.createdAt));

  const mappedLines = await mapLinesWithUnits(client, lines);
  const receipts = await listImportOrderReceiptsByOrder(client, id, lines);
  const linkedGoodsReceipts = await listGoodsReceiptsByImportOrderId(
    client,
    id,
  );

  const today = startOfTodayUtc();
  const totalRemaining = lines.reduce(
    (sum, line) => sum + importLineInTransitRemaining(line),
    0,
  );
  const inTransitValueLak = lines
    .reduce((sum, line) => sum + importLineInTransitValueLak(line), 0)
    .toFixed(2);

  const isOverdue =
    row.expectedArrivalDate != null &&
    row.expectedArrivalDate < today &&
    (OPEN_IMPORT_STATUSES as readonly string[]).includes(row.status) &&
    totalRemaining > 0;

  const { linePreviewItems, linePreviewMoreCount } =
    buildLinePreviewFromLines(lines);

  return {
    id: row.id,
    orderNumber: row.orderNumber,
    externalOrderNo: row.externalOrderNo,
    source: row.source as ImportOrderPhase13DetailDTO["source"],
    status: row.status as ImportOrderPhase13DetailDTO["status"],
    currency: row.currency,
    exchangeRate: row.exchangeRate,
    shippingTotalLak: row.shippingTotalLak,
    goodsSubtotalForeign: row.goodsSubtotalForeign,
    goodsSubtotalLak: row.goodsSubtotalLak,
    landedTotalLak: row.landedTotalLak,
    orderedAt: row.orderedAt,
    expectedArrivalDate: row.expectedArrivalDate,
    lineCount: lines.length,
    totalOrdered: lines.reduce((s, l) => s + l.quantityOrdered, 0),
    totalReceived: lines.reduce((s, l) => s + l.quantityReceived, 0),
    totalRemaining,
    inTransitValueLak,
    linePreviewItems,
    linePreviewMoreCount,
    pendingLineCount: lines.filter((l) => l.matchStatus === "pending").length,
    isOverdue,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    note: row.note,
    createdBy: row.createdBy,
    lines: mappedLines,
    trackings: trackings.map(mapTrackingRow),
    receipts,
    goodsReceipts: linkedGoodsReceipts,
  };
}

export type Phase13ImportOrderHeaderInsert = {
  orderNumber: string;
  externalOrderNo: string;
  source: string;
  sourceApp: string;
  status: string;
  currency: string;
  exchangeRate: string;
  goodsSubtotalForeign: string;
  goodsSubtotalLak: string;
  shippingTotalLak: string;
  landedTotalLak: string;
  orderedAt: Date | null;
  expectedArrivalDate: Date | null;
  note: string | null;
  createdBy: string | null;
};

export type Phase13ImportOrderLineInsert = {
  variantId: string | null;
  matchStatus: ImportOrderLineMatchStatus;
  externalTitle?: string | null;
  externalUrl?: string | null;
  externalImageKey?: string | null;
  matchedAt?: Date | null;
  matchedBy?: string | null;
  skippedAt?: Date | null;
  skipNote?: string | null;
  description: string | null;
  quantityOrdered: number;
  unitPriceForeign: string;
  goodsCostForeign: string;
  goodsCostLak: string;
  allocatedShippingLak: string;
  landedCostLak: string;
  unitLandedCostLak: string;
  productName: string;
  variantName: string;
};

function mapLineInsertValues(
  importOrderId: string,
  line: Phase13ImportOrderLineInsert,
  now: Date,
) {
  return {
    importOrderId,
    variantId: line.variantId,
    matchStatus: line.matchStatus,
    externalTitle: line.externalTitle ?? null,
    externalUrl: line.externalUrl ?? null,
    externalImageKey: line.externalImageKey ?? null,
    matchedAt: line.matchedAt ?? null,
    matchedBy: line.matchedBy ?? null,
    skippedAt: line.skippedAt ?? null,
    skipNote: line.skipNote ?? null,
    description: line.description,
    quantityOrdered: line.quantityOrdered,
    unitPriceForeign: line.unitPriceForeign,
    goodsCostForeign: line.goodsCostForeign,
    goodsCostLak: line.goodsCostLak,
    allocatedShippingLak: line.allocatedShippingLak,
    landedCostLak: line.landedCostLak,
    unitLandedCostLak: line.unitLandedCostLak,
    productName: line.productName,
    variantName: line.variantName,
    createdAt: now,
    updatedAt: now,
  };
}

export async function insertPhase13ImportOrder(
  client: DbTransaction,
  header: Phase13ImportOrderHeaderInsert,
  lines: Phase13ImportOrderLineInsert[],
  trackings: {
    trackingNo: string;
    carrierName: string | null;
    status: string;
    shippedAt: Date | null;
    arrivedAt: Date | null;
    note: string | null;
  }[] = [],
) {
  const now = nowDate();
  const [order] = await client
    .insert(importOrders)
    .values({
      orderNumber: header.orderNumber,
      externalOrderNo: header.externalOrderNo,
      source: header.source,
      sourceApp: header.sourceApp,
      externalRef: header.externalOrderNo,
      status: header.status,
      currency: header.currency,
      exchangeRate: header.exchangeRate,
      goodsSubtotalForeign: header.goodsSubtotalForeign,
      goodsSubtotalLak: header.goodsSubtotalLak,
      shippingTotalLak: header.shippingTotalLak,
      landedTotalLak: header.landedTotalLak,
      orderedAt: header.orderedAt,
      expectedArrivalDate: header.expectedArrivalDate,
      expectedAt: header.expectedArrivalDate,
      note: header.note,
      createdBy: header.createdBy,
      createdAt: now,
      updatedAt: now,
    })
    .returning();
  if (!order) throw new Error("Failed to create import order");

  if (lines.length > 0) {
    await client.insert(importOrderLines).values(
      lines.map((l) => mapLineInsertValues(order.id, l, now)),
    );
  }

  if (trackings.length > 0) {
    await client.insert(importOrderTrackings).values(
      trackings.map((t) => ({
        importOrderId: order.id,
        trackingNo: t.trackingNo,
        carrierName: t.carrierName,
        status: t.status,
        shippedAt: t.shippedAt,
        arrivedAt: t.arrivedAt,
        note: t.note,
        createdAt: now,
        updatedAt: now,
      })),
    );
  }

  return order;
}

export async function updatePhase13ImportOrderHeader(
  client: DbTransaction,
  id: string,
  patch: Partial<Phase13ImportOrderHeaderInsert> & { status?: string },
) {
  const legacyPatch: Record<string, unknown> = {
    ...patch,
    updatedAt: nowDate(),
  };
  if (patch.expectedArrivalDate !== undefined) {
    legacyPatch.expectedAt = patch.expectedArrivalDate;
  }
  if (patch.externalOrderNo !== undefined) {
    legacyPatch.externalRef = patch.externalOrderNo;
  }
  await client
    .update(importOrders)
    .set(legacyPatch)
    .where(eq(importOrders.id, id));
}

export async function replacePhase13ImportOrderLines(
  client: DbTransaction,
  importOrderId: string,
  lines: Phase13ImportOrderLineInsert[],
) {
  const blockedLines = await client
    .select({ id: importOrderLines.id })
    .from(importOrderLines)
    .where(
      and(
        eq(importOrderLines.importOrderId, importOrderId),
        sql`(${importOrderLines.quantityReceived} > 0 OR ${importOrderLines.shortQuantity} > 0)`,
      ),
    );
  if (blockedLines.length > 0) {
    throw new Error("ບໍ່ສາມາດລຶບຫຼືແທນທີ່ລາຍການທີ່ຮັບເຂົ້າແລ້ວ");
  }

  await client
    .delete(importOrderLines)
    .where(eq(importOrderLines.importOrderId, importOrderId));

  if (lines.length === 0) return;

  const now = nowDate();
  await client.insert(importOrderLines).values(
    lines.map((l) => mapLineInsertValues(importOrderId, l, now)),
  );
}

export async function insertImportOrderTracking(
  client: DbTransaction,
  importOrderId: string,
  input: {
    trackingNo: string;
    carrierName: string | null;
    status: string;
    shippedAt: Date | null;
    arrivedAt: Date | null;
    note: string | null;
  },
) {
  const now = nowDate();
  const [row] = await client
    .insert(importOrderTrackings)
    .values({
      importOrderId,
      trackingNo: input.trackingNo,
      carrierName: input.carrierName,
      status: input.status,
      shippedAt: input.shippedAt,
      arrivedAt: input.arrivedAt,
      note: input.note,
      createdAt: now,
      updatedAt: now,
    })
    .returning();
  return row;
}

export async function updateImportOrderTrackingById(
  client: DbTransaction,
  trackingId: string,
  importOrderId: string,
  patch: Partial<{
    trackingNo: string;
    carrierName: string | null;
    status: string;
    shippedAt: Date | null;
    arrivedAt: Date | null;
    note: string | null;
  }>,
) {
  await client
    .update(importOrderTrackings)
    .set({ ...patch, updatedAt: nowDate() })
    .where(
      and(
        eq(importOrderTrackings.id, trackingId),
        eq(importOrderTrackings.importOrderId, importOrderId),
      ),
    );
}

export async function getImportOrderTrackingById(
  client: DbLike,
  importOrderId: string,
  trackingId: string,
) {
  const [row] = await client
    .select()
    .from(importOrderTrackings)
    .where(
      and(
        eq(importOrderTrackings.id, trackingId),
        eq(importOrderTrackings.importOrderId, importOrderId),
      ),
    );
  return row ?? null;
}

export async function countImportOrderReceipts(
  client: DbLike,
  importOrderId: string,
): Promise<number> {
  const [row] = await client
    .select({ count: sql<number>`count(*)::int` })
    .from(importOrderReceipts)
    .where(
      and(
        eq(importOrderReceipts.importOrderId, importOrderId),
        eq(importOrderReceipts.status, "confirmed"),
      ),
    );
  return row?.count ?? 0;
}

async function listImportOrderReceiptsByOrder(
  client: DbLike,
  importOrderId: string,
  orderLines: Array<{
    id: string;
    productName: string;
    variantName: string;
  }>,
): Promise<ImportOrderReceiptDTO[]> {
  const receipts = await client
    .select()
    .from(importOrderReceipts)
    .where(eq(importOrderReceipts.importOrderId, importOrderId))
    .orderBy(desc(importOrderReceipts.receivedAt));

  if (receipts.length === 0) return [];

  const receiptIds = receipts.map((r) => r.id);
  const receiptLines = await client
    .select()
    .from(importOrderReceiptLines)
    .where(inArray(importOrderReceiptLines.receiptId, receiptIds));

  const lineNameById = new Map(orderLines.map((l) => [l.id, l] as const));

  return receipts.map((receipt) => {
    const lines = receiptLines
      .filter((l) => l.receiptId === receipt.id)
      .map((l) => {
        const orderLine = lineNameById.get(l.importOrderLineId);
        return {
          id: l.id,
          importOrderLineId: l.importOrderLineId,
          variantId: l.variantId,
          productName: orderLine?.productName ?? "—",
          variantName: orderLine?.variantName ?? "—",
          receivedQuantity: l.receivedQuantity,
          unitLandedCostLak: l.unitLandedCostLak,
          landedCostLak: l.landedCostLak,
          stockLotId: l.stockLotId,
        };
      });
    return {
      id: receipt.id,
      importOrderId: receipt.importOrderId,
      status: receipt.status as ImportOrderReceiptDTO["status"],
      receivedAt: receipt.receivedAt,
      note: receipt.note,
      createdBy: receipt.createdBy,
      voidedAt: receipt.voidedAt,
      voidedBy: receipt.voidedBy,
      createdAt: receipt.createdAt,
      totalReceived: lines.reduce((s, l) => s + l.receivedQuantity, 0),
      lines,
    };
  });
}

export async function getImportOrderReceiptForVoid(
  client: DbLike,
  importOrderId: string,
  receiptId: string,
) {
  const [receipt] = await client
    .select()
    .from(importOrderReceipts)
    .where(
      and(
        eq(importOrderReceipts.id, receiptId),
        eq(importOrderReceipts.importOrderId, importOrderId),
      ),
    );
  if (!receipt) return null;

  const lines = await client
    .select()
    .from(importOrderReceiptLines)
    .where(eq(importOrderReceiptLines.receiptId, receiptId));

  const lotIds = lines
    .map((l) => l.stockLotId)
    .filter((id): id is string => Boolean(id));

  const lots =
    lotIds.length > 0
      ? await client
          .select()
          .from(stockLots)
          .where(inArray(stockLots.id, lotIds))
      : [];

  return { receipt, lines, lots };
}

export async function markImportOrderReceiptVoid(
  client: DbTransaction,
  receiptId: string,
  voidedBy: string | null,
) {
  await client
    .update(importOrderReceipts)
    .set({
      status: "void",
      voidedAt: nowDate(),
      voidedBy,
    })
    .where(eq(importOrderReceipts.id, receiptId));
}

export async function clearImportOrderReceiptLineLotRefs(
  client: DbTransaction,
  receiptLineIds: string[],
) {
  if (receiptLineIds.length === 0) return;
  await client
    .update(importOrderReceiptLines)
    .set({ stockLotId: null, stockMovementId: null })
    .where(inArray(importOrderReceiptLines.id, receiptLineIds));
}

export async function clearStockLotImportReceiptLineRefs(
  client: DbTransaction,
  lotIds: string[],
) {
  if (lotIds.length === 0) return;
  await client
    .update(stockLots)
    .set({ importOrderReceiptLineId: null })
    .where(inArray(stockLots.id, lotIds));
}

export async function deleteStockLotsByIds(
  client: DbTransaction,
  lotIds: string[],
) {
  if (lotIds.length === 0) return;
  await client.delete(stockLots).where(inArray(stockLots.id, lotIds));
}

export async function insertImportOrderReceipt(
  client: DbTransaction,
  input: {
    importOrderId: string;
    receivedAt: Date;
    note: string | null;
    createdBy: string | null;
  },
) {
  const [receipt] = await client
    .insert(importOrderReceipts)
    .values({
      importOrderId: input.importOrderId,
      status: "confirmed",
      receivedAt: input.receivedAt,
      note: input.note,
      createdBy: input.createdBy,
      createdAt: nowDate(),
    })
    .returning();
  if (!receipt) throw new Error("Failed to create import order receipt");
  return receipt;
}

export async function insertImportOrderReceiptLine(
  client: DbTransaction,
  input: {
    receiptId: string;
    importOrderLineId: string;
    variantId: string;
    receivedQuantity: number;
    unitLandedCostLak: string;
    landedCostLak: string;
    stockLotId: string | null;
    stockMovementId: string | null;
  },
) {
  const [line] = await client
    .insert(importOrderReceiptLines)
    .values({
      receiptId: input.receiptId,
      importOrderLineId: input.importOrderLineId,
      variantId: input.variantId,
      receivedQuantity: input.receivedQuantity,
      unitLandedCostLak: input.unitLandedCostLak,
      landedCostLak: input.landedCostLak,
      stockLotId: input.stockLotId,
      stockMovementId: input.stockMovementId,
      createdAt: nowDate(),
    })
    .returning();
  if (!line) throw new Error("Failed to create import order receipt line");
  return line;
}

export async function insertImportOrderStockLot(
  client: DbTransaction,
  input: {
    importOrderReceiptLineId: string;
    variantId: string;
    quantityReceived: number;
    unitLandedCost: string;
    unitPurchaseCost: string;
    receivedAt: Date;
  },
) {
  const [lot] = await client
    .insert(stockLots)
    .values({
      importOrderReceiptLineId: input.importOrderReceiptLineId,
      variantId: input.variantId,
      quantityReceived: input.quantityReceived,
      quantityRemaining: input.quantityReceived,
      unitLandedCost: input.unitLandedCost,
      unitPurchaseCost: input.unitPurchaseCost,
      receivedAt: input.receivedAt,
    })
    .returning();
  if (!lot) throw new Error("Failed to create stock lot");
  return lot;
}

export async function incrementImportOrderLineReceived(
  client: DbTransaction,
  importOrderLineId: string,
  quantity: number,
) {
  const [line] = await client
    .select({
      id: importOrderLines.id,
      quantityReceived: importOrderLines.quantityReceived,
    })
    .from(importOrderLines)
    .where(eq(importOrderLines.id, importOrderLineId));
  if (!line) throw new Error("Import order line not found");

  await client
    .update(importOrderLines)
    .set({
      quantityReceived: line.quantityReceived + quantity,
      updatedAt: nowDate(),
    })
    .where(eq(importOrderLines.id, importOrderLineId));
}

export async function decrementImportOrderLineReceived(
  client: DbTransaction,
  importOrderLineId: string,
  quantity: number,
) {
  const [line] = await client
    .select({
      id: importOrderLines.id,
      quantityReceived: importOrderLines.quantityReceived,
    })
    .from(importOrderLines)
    .where(eq(importOrderLines.id, importOrderLineId));
  if (!line) throw new Error("Import order line not found");

  const next = line.quantityReceived - quantity;
  if (next < 0) {
    throw new Error("Cannot reverse receive below zero");
  }

  await client
    .update(importOrderLines)
    .set({
      quantityReceived: next,
      updatedAt: nowDate(),
    })
    .where(eq(importOrderLines.id, importOrderLineId));
}

export async function setImportOrderLineReceived(
  client: DbTransaction,
  importOrderLineId: string,
  quantityReceived: number,
) {
  await client
    .update(importOrderLines)
    .set({
      quantityReceived,
      updatedAt: nowDate(),
    })
    .where(eq(importOrderLines.id, importOrderLineId));
}

export async function incrementImportOrderLineShort(
  client: DbTransaction,
  importOrderLineId: string,
  shortQuantity: number,
) {
  const [line] = await client
    .select({
      id: importOrderLines.id,
      shortQuantity: importOrderLines.shortQuantity,
    })
    .from(importOrderLines)
    .where(eq(importOrderLines.id, importOrderLineId));
  if (!line) throw new Error("Import order line not found");

  await client
    .update(importOrderLines)
    .set({
      shortQuantity: line.shortQuantity + shortQuantity,
      updatedAt: nowDate(),
    })
    .where(eq(importOrderLines.id, importOrderLineId));
}

export async function getImportOrderLineById(
  client: DbLike,
  importOrderLineId: string,
  importOrderId: string,
) {
  const [row] = await client
    .select()
    .from(importOrderLines)
    .where(
      and(
        eq(importOrderLines.id, importOrderLineId),
        eq(importOrderLines.importOrderId, importOrderId),
      ),
    );
  if (!row) return null;
  const [mapped] = await mapLinesWithUnits(client, [row]);
  return mapped ?? null;
}

export async function updateImportOrderReceiptLineRefs(
  client: DbTransaction,
  receiptLineId: string,
  refs: { stockLotId: string; stockMovementId: string | null },
) {
  await client
    .update(importOrderReceiptLines)
    .set({
      stockLotId: refs.stockLotId,
      stockMovementId: refs.stockMovementId,
    })
    .where(eq(importOrderReceiptLines.id, receiptLineId));
}

export async function applyPhase13ImportOrderLineCostUpdates(
  client: DbTransaction,
  updates: Array<{
    lineId: string;
    quantityOrdered?: number;
    unitPriceForeign: string;
    goodsCostForeign: string;
    goodsCostLak: string;
    allocatedShippingLak: string;
    landedCostLak: string;
    unitLandedCostLak: string;
  }>,
) {
  const now = nowDate();
  for (const row of updates) {
    await client
      .update(importOrderLines)
      .set({
        ...(row.quantityOrdered !== undefined
          ? { quantityOrdered: row.quantityOrdered }
          : {}),
        unitPriceForeign: row.unitPriceForeign,
        goodsCostForeign: row.goodsCostForeign,
        goodsCostLak: row.goodsCostLak,
        allocatedShippingLak: row.allocatedShippingLak,
        landedCostLak: row.landedCostLak,
        unitLandedCostLak: row.unitLandedCostLak,
        updatedAt: now,
      })
      .where(eq(importOrderLines.id, row.lineId));
  }
}

export async function listOverduePhase13ImportOrders(client: DbLike) {
  return listPhase13ImportOrders(client, { overdueOnly: true });
}

export async function getImportOrderInTransitValueSummary(client: DbLike) {
  const openLines = await client
    .select({
      source: importOrders.source,
      status: importOrders.status,
      variantId: importOrderLines.variantId,
      productName: importOrderLines.productName,
      variantName: importOrderLines.variantName,
      unitLandedCostLak: importOrderLines.unitLandedCostLak,
      remaining: remainingQtyExpr,
      expectedArrivalDate: importOrders.expectedArrivalDate,
    })
    .from(importOrderLines)
    .innerJoin(
      importOrders,
      eq(importOrderLines.importOrderId, importOrders.id),
    )
    .where(
      and(
        inArray(importOrders.status, [...OPEN_IMPORT_STATUSES]),
        sql`${remainingQtyExpr} > 0`,
        matchedImportLineWhere(),
      ),
    );

  const today = startOfTodayUtc();
  let totalInTransitValueLak = 0;
  let overdueInTransitValueLak = 0;
  const bySource = new Map<string, number>();
  const byStatus = new Map<string, number>();
  const byVariant = new Map<
    string,
    {
      variantId: string;
      productName: string;
      variantName: string;
      valueLak: number;
    }
  >();

  for (const row of openLines) {
    const remaining = Math.max(0, row.remaining);
    const value = Number(row.unitLandedCostLak) * remaining;
    totalInTransitValueLak += value;
    bySource.set(row.source, (bySource.get(row.source) ?? 0) + value);
    byStatus.set(row.status, (byStatus.get(row.status) ?? 0) + value);

    const variantKey = row.variantId;
    const existing = byVariant.get(variantKey);
    if (existing) {
      existing.valueLak += value;
    } else {
      byVariant.set(variantKey, {
        variantId: row.variantId,
        productName: row.productName,
        variantName: row.variantName,
        valueLak: value,
      });
    }

    if (
      row.expectedArrivalDate &&
      row.expectedArrivalDate < today &&
      remaining > 0
    ) {
      overdueInTransitValueLak += value;
    }
  }

  const topVariants = [...byVariant.values()]
    .sort((a, b) => b.valueLak - a.valueLak)
    .slice(0, 10)
    .map((v) => ({
      ...v,
      inTransitValueLak: v.valueLak.toFixed(2),
    }));

  return {
    totalInTransitValueLak: totalInTransitValueLak.toFixed(2),
    bySource: [...bySource.entries()].map(([source, valueLak]) => ({
      source: source as ImportOrderPhase13ListItemDTO["source"],
      valueLak: valueLak.toFixed(2),
    })),
    byStatus: [...byStatus.entries()].map(([status, valueLak]) => ({
      status: status as ImportOrderPhase13ListItemDTO["status"],
      valueLak: valueLak.toFixed(2),
    })),
    topVariants,
    overdueInTransitValueLak: overdueInTransitValueLak.toFixed(2),
  };
}

export type VariantIncomingRowDTO = {
  variantId: string;
  incomingQuantity: number;
  inTransitValueLak: string;
};

export async function getIncomingQuantityByVariantIds(
  client: DbLike,
  variantIds: string[],
): Promise<VariantIncomingRowDTO[]> {
  if (variantIds.length === 0) return [];

  const rows = await client
    .select({
      variantId: importOrderLines.variantId,
      incomingQuantity: sql<number>`coalesce(sum(greatest(
        ${importOrderLines.quantityOrdered}
        - ${importOrderLines.quantityReceived}
        - ${importOrderLines.shortQuantity},
        0
      )), 0)::int`,
      inTransitValueLak: sql<string>`coalesce(sum(
        (${importOrderLines.unitLandedCostLak})::numeric
        * greatest(
          ${importOrderLines.quantityOrdered}
          - ${importOrderLines.quantityReceived}
          - ${importOrderLines.shortQuantity},
          0
        )
      ), 0)::text`,
    })
    .from(importOrderLines)
    .innerJoin(
      importOrders,
      eq(importOrderLines.importOrderId, importOrders.id),
    )
    .where(
      and(
        inArray(importOrderLines.variantId, variantIds),
        inArray(importOrders.status, [...OPEN_IMPORT_STATUSES]),
        sql`${remainingQtyExpr} > 0`,
        matchedImportLineWhere(),
      ),
    )
    .groupBy(importOrderLines.variantId);

  return rows.map((row) => ({
    variantId: row.variantId,
    incomingQuantity: row.incomingQuantity,
    inTransitValueLak: row.inTransitValueLak,
  }));
}

export async function listAllIncomingQuantityByVariant(
  client: DbLike,
): Promise<Map<string, number>> {
  const rows = await client
    .select({
      variantId: importOrderLines.variantId,
      incomingQuantity: sql<number>`coalesce(sum(greatest(
        ${importOrderLines.quantityOrdered}
        - ${importOrderLines.quantityReceived}
        - ${importOrderLines.shortQuantity},
        0
      )), 0)::int`,
    })
    .from(importOrderLines)
    .innerJoin(
      importOrders,
      eq(importOrderLines.importOrderId, importOrders.id),
    )
    .where(
      and(
        inArray(importOrders.status, [...OPEN_IMPORT_STATUSES]),
        sql`${remainingQtyExpr} > 0`,
        matchedImportLineWhere(),
      ),
    )
    .groupBy(importOrderLines.variantId);

  return new Map(
    rows.map((row) => [row.variantId, row.incomingQuantity] as const),
  );
}

export async function listOverdueImportOrderAlerts(client: DbLike) {
  const orders = await listOverduePhase13ImportOrders(client);
  const today = startOfTodayUtc();

  return orders
    .filter(
      (order): order is typeof order & { expectedArrivalDate: Date } =>
        order.expectedArrivalDate != null,
    )
    .map((order) => {
      const daysOverdue = Math.max(
        0,
        Math.floor(
          (today.getTime() - order.expectedArrivalDate.getTime()) /
            (24 * 60 * 60 * 1000),
        ),
      );

      return {
        importOrderId: order.id,
        externalOrderNo: order.externalOrderNo,
        source: order.source,
        expectedArrivalDate: order.expectedArrivalDate,
        daysOverdue,
        remainingQuantity: order.totalRemaining,
        inTransitValueLak: order.inTransitValueLak,
      };
    });
}

export async function getLastImportPricesByVariantIds(
  client: DbLike,
  variantIds: string[],
): Promise<VariantLastImportPriceDTO[]> {
  if (variantIds.length === 0) return [];

  const rows = await client
    .select({
      variantId: importOrderLines.variantId,
      unitPriceForeign: importOrderLines.unitPriceForeign,
      source: importOrders.source,
      orderedAt: importOrders.orderedAt,
      createdAt: importOrders.createdAt,
    })
    .from(importOrderLines)
    .innerJoin(
      importOrders,
      eq(importOrderLines.importOrderId, importOrders.id),
    )
    .where(
      and(
        inArray(importOrderLines.variantId, variantIds),
        sql`${importOrders.status} != 'cancelled'`,
      ),
    )
    .orderBy(desc(importOrders.orderedAt), desc(importOrders.createdAt));

  const latestByVariant = new Map<string, (typeof rows)[number]>();
  for (const row of rows) {
    if (!latestByVariant.has(row.variantId)) {
      latestByVariant.set(row.variantId, row);
    }
  }

  return variantIds.map((variantId) => {
    const row = latestByVariant.get(variantId);
    return {
      variantId,
      unitPriceForeign: row?.unitPriceForeign ?? null,
      source: (row?.source as VariantLastImportPriceDTO["source"]) ?? null,
      orderedAt: row?.orderedAt ?? null,
    };
  });
}

function buildInTransitLineConditions(query: InTransitReportQueryDTO) {
  const conditions = [
    inArray(importOrders.status, [...OPEN_IMPORT_STATUSES]),
    sql`${remainingQtyExpr} > 0`,
    matchedImportLineWhere(),
  ];
  if (query.source) conditions.push(eq(importOrders.source, query.source));
  if (query.status) conditions.push(eq(importOrders.status, query.status));
  if (query.overdueOnly) {
    conditions.push(lt(importOrders.expectedArrivalDate, startOfTodayUtc()));
  }
  return conditions;
}

export async function listInTransitReportByOrder(
  client: DbLike,
  query: InTransitReportQueryDTO,
): Promise<ImportOrderPhase13ListItemDTO[]> {
  const orders = await listPhase13ImportOrders(client, {
    source: query.source,
    status: query.status,
    overdueOnly: query.overdueOnly,
  });

  return orders.filter(
    (row) =>
      (OPEN_IMPORT_STATUSES as readonly string[]).includes(row.status) &&
      row.totalRemaining > 0,
  );
}

export async function listInTransitReportByVariant(
  client: DbLike,
  query: InTransitReportQueryDTO,
): Promise<InTransitReportByVariantRowDTO[]> {
  const rows = await client
    .select({
      variantId: importOrderLines.variantId,
      productName: importOrderLines.productName,
      variantName: importOrderLines.variantName,
      remaining: remainingQtyExpr,
      unitLandedCostLak: importOrderLines.unitLandedCostLak,
      importOrderId: importOrders.id,
    })
    .from(importOrderLines)
    .innerJoin(
      importOrders,
      eq(importOrderLines.importOrderId, importOrders.id),
    )
    .where(and(...buildInTransitLineConditions(query)));

  const byVariant = new Map<
    string,
    InTransitReportByVariantRowDTO & { orderIds: Set<string> }
  >();

  for (const row of rows) {
    const remaining = Math.max(0, row.remaining);
    if (remaining <= 0) continue;

    const value = Number(row.unitLandedCostLak) * remaining;
    const existing = byVariant.get(row.variantId);
    if (existing) {
      existing.remainingQuantity += remaining;
      existing.inTransitValueLak = (
        Number(existing.inTransitValueLak) + value
      ).toFixed(2);
      existing.orderIds.add(row.importOrderId);
      existing.orderCount = existing.orderIds.size;
    } else {
      byVariant.set(row.variantId, {
        variantId: row.variantId,
        productName: row.productName,
        variantName: row.variantName,
        remainingQuantity: remaining,
        inTransitValueLak: value.toFixed(2),
        orderCount: 1,
        orderIds: new Set([row.importOrderId]),
      });
    }
  }

  return [...byVariant.values()]
    .map(({ orderIds: _orderIds, ...row }) => row)
    .sort(
      (a, b) =>
        Number(b.inTransitValueLak) - Number(a.inTransitValueLak) ||
        a.productName.localeCompare(b.productName),
    );
}

export async function getVariantImportPriceHistory(
  client: DbLike,
  variantId: string,
): Promise<VariantImportPriceHistoryRowDTO[]> {
  const rows = await client
    .select({
      importOrderId: importOrders.id,
      externalOrderNo: importOrders.externalOrderNo,
      source: importOrders.source,
      orderedAt: importOrders.orderedAt,
      unitPriceForeign: importOrderLines.unitPriceForeign,
      currency: importOrders.currency,
      exchangeRate: importOrders.exchangeRate,
      unitLandedCostLak: importOrderLines.unitLandedCostLak,
      orderedQuantity: importOrderLines.quantityOrdered,
      createdAt: importOrders.createdAt,
    })
    .from(importOrderLines)
    .innerJoin(
      importOrders,
      eq(importOrderLines.importOrderId, importOrders.id),
    )
    .where(
      and(
        eq(importOrderLines.variantId, variantId),
        sql`${importOrders.status} != 'cancelled'`,
      ),
    )
    .orderBy(desc(importOrders.orderedAt), desc(importOrders.createdAt));

  return rows.map((row) => ({
    importOrderId: row.importOrderId,
    externalOrderNo: row.externalOrderNo,
    source: row.source as VariantImportPriceHistoryRowDTO["source"],
    orderedAt: row.orderedAt,
    unitPriceForeign: row.unitPriceForeign,
    currency: row.currency,
    exchangeRate: row.exchangeRate,
    unitLandedCostLak: row.unitLandedCostLak,
    orderedQuantity: row.orderedQuantity,
  }));
}

export async function getPhase13ImportOrderLineById(
  client: DbLike,
  importOrderId: string,
  lineId: string,
) {
  const [row] = await client
    .select()
    .from(importOrderLines)
    .where(
      and(
        eq(importOrderLines.id, lineId),
        eq(importOrderLines.importOrderId, importOrderId),
      ),
    )
    .limit(1);
  return row ?? null;
}

export async function updateImportOrderLineMatch(
  client: DbTransaction,
  lineId: string,
  input: {
    variantId: string;
    productName: string;
    variantName: string;
    description: string | null;
    unitPriceForeign: string;
    goodsCostForeign: string;
    goodsCostLak: string;
    allocatedShippingLak: string;
    landedCostLak: string;
    unitLandedCostLak: string;
    matchedBy?: string | null;
  },
) {
  const now = nowDate();
  await client
    .update(importOrderLines)
    .set({
      variantId: input.variantId,
      matchStatus: "matched",
      externalTitle: null,
      externalUrl: null,
      externalImageKey: null,
      matchedAt: now,
      matchedBy: input.matchedBy ?? null,
      skippedAt: null,
      skipNote: null,
      productName: input.productName,
      variantName: input.variantName,
      description: input.description,
      unitPriceForeign: input.unitPriceForeign,
      goodsCostForeign: input.goodsCostForeign,
      goodsCostLak: input.goodsCostLak,
      allocatedShippingLak: input.allocatedShippingLak,
      landedCostLak: input.landedCostLak,
      unitLandedCostLak: input.unitLandedCostLak,
      updatedAt: now,
    })
    .where(eq(importOrderLines.id, lineId));
}

export async function updateImportOrderLineSkip(
  client: DbTransaction,
  lineId: string,
  note: string | null,
) {
  const now = nowDate();
  await client
    .update(importOrderLines)
    .set({
      matchStatus: "skipped",
      skippedAt: now,
      skipNote: note,
      updatedAt: now,
    })
    .where(eq(importOrderLines.id, lineId));
}
